import { useState } from 'react'
import { createPortal } from 'react-dom'

import type { Poll } from '../../api/types'
import { XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { useTexte } from '../../texte'

/**
 * Closing a poll, and marking its right answer (#143, 1.138.0).
 *
 * From use: "beim Schließen einer Umfrage solle es eine Option geben die
 * richtige Antwort zu markieren", and decided with it: marking afterwards
 * too (`mode: 'mark'`), several right answers with multiple choice. The mark
 * is optional; closing without one is closing as before.
 *
 * **The sheet starts from what is stored.** The author gets the marks of an
 * open poll as well (`Abstimmung::ergebnis()`), so a poll opened again and
 * closed again does not lose its answer by the way.
 *
 * With a single choice the boxes behave like a choice of one that can be
 * taken back: a second answer replaces the first, a tap on the ticked one
 * clears it. Radio buttons could not be cleared.
 *
 * **At the body and in the house build** (`createPortal`, `.bogen`,
 * `--gegliedert`), like `Abstimmungsblatt` next to it.
 */
export function PollAnswerSheet({
  poll,
  mode,
  busy,
  onDone,
  onZu,
}: {
  poll: Poll
  mode: 'close' | 'mark'
  busy: boolean
  onDone: (correctOptionIds: number[]) => void
  onZu: () => void
}) {
  const t = useTexte()
  useEscape(onZu)
  const [chosen, setChosen] = useState<number[]>(() =>
    poll.options.filter((option) => option.correct === true).map((option) => option.id),
  )

  function toggle(id: number): void {
    setChosen((now) => {
      if (now.includes(id)) return now.filter((x) => x !== id)
      return poll.multi ? [...now, id] : [id]
    })
  }

  const title = mode === 'close' ? t.chat.pollCloseTitle : t.chat.pollRightTitle

  return createPortal(
    <div className="bogen" role="dialog" aria-modal="true" aria-label={title} data-poll-sheet={mode}>
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{title}</h3>
          <button type="button" className="icon-button" onClick={onZu} aria-label={t.allgemein.schliessen}>
            <XIcon size={20} />
          </button>
        </div>

        <div className="bogen__rolle">
          {mode === 'close' && <p className="feld__hinweis">{t.chat.pollCloseHint}</p>}
          <p className="field__label">{t.chat.pollRightHeading(poll.multi)}</p>
          <ul className="richtig__liste">
            {poll.options.map((option) => (
              <li key={option.id}>
                <label className="row row--eng row--luft">
                  <input
                    type="checkbox"
                    checked={chosen.includes(option.id)}
                    onChange={() => toggle(option.id)}
                    data-poll-right={option.id}
                  />
                  <span>{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div className="bogen__fuss">
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => onDone(chosen)}
            data-poll-sheet-done=""
          >
            {mode === 'close' ? t.allgemein.schliessen : t.chat.pollRightSave}
          </button>
          <button type="button" className="button button--outline" onClick={onZu}>
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
